
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { TimelineEntry } from "@/types";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Edit, Globe, Lock, Users, X, Trash2, EyeOff, Eye } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useEnhancedTimelineEntryManagement } from "@/hooks/useEnhancedTimelineEntryManagement";
import ActionDrawer from "./ui/action-drawer";
import { sanitizeUserInput } from "@/utils/securityUtils";
import ImageCarousel from "./ui/ImageCarousel";

interface SecureEntryDrawerProps {
  entry: TimelineEntry;
  isOpen: boolean;
  onClose: () => void;
  timelineOwnerId?: string;
}

const SecureEntryDrawer = ({ entry, isOpen, onClose, timelineOwnerId }: SecureEntryDrawerProps) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { user } = useAuth();
  const { deleteEntry, hideEntry, isDeleting, isHiding, canDeleteEntry, getDeleteButtonText } = useEnhancedTimelineEntryManagement(timelineOwnerId);

  const formatDate = (date: string) => {
    return format(new Date(date), "dd. MMMM yyyy", { locale: de });
  };

  const getPrivacyIcon = () => {
    switch (entry.privacy_level) {
      case "private":
        return <Lock className="h-4 w-4" />;
      case "friends":
        return <Users className="h-4 w-4" />;
      case "public":
        return <Globe className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getPrivacyText = () => {
    switch (entry.privacy_level) {
      case "private":
        return "Nur ich";
      case "friends":
        return "Freunde";
      case "public":
        return "Öffentlich";
      default:
        return "";
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!user?.id) {
      return;
    }
    
    const success = await deleteEntry(entry.id, user.id, entry.creator_id, entry.user_id);
    if (success) {
      onClose();
    }
  };

  const handleHide = async () => {
    const isCurrentlyHidden = !entry.is_approved;
    const success = await hideEntry(entry.id, entry.user_id, !isCurrentlyHidden);
    if (success) {
      onClose();
    }
  };

  // Check permissions using enhanced logic
  const canDelete = user?.id ? canDeleteEntry(entry.creator_id, entry.user_id, user.id) : false;
  const canHide = user?.id === timelineOwnerId;
  const isHidden = !entry.is_approved;

  // Sanitize content for display
  const sanitizedTitle = sanitizeUserInput(entry.title || '');
  const sanitizedContent = sanitizeUserInput(entry.content || '');

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="p-6 pb-0">
            <div className="flex justify-between items-center">
              <DrawerTitle className="text-2xl font-bold">{sanitizedTitle}</DrawerTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <DrawerDescription>
              <span className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                <span>{formatDate(entry.date)}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  {getPrivacyIcon()}
                  <span>{getPrivacyText()}</span>
                </span>
                {isHidden && (
                  <>
                    <span>•</span>
                    <span className="text-orange-600">Hidden</span>
                  </>
                )}
              </span>
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto">
            {entry.photos && entry.photos.length > 0 && (
              <div className="mt-4 px-4">
                <ImageCarousel 
                  images={entry.photos} 
                  showControls={true}
                  showDots={true}
                  onImageChange={setCurrentPhotoIndex}
                />
              </div>
            )}

            <div className="p-6">
              {sanitizedContent && <p className="text-gray-700 whitespace-pre-line">{sanitizedContent}</p>}
              
              <div className="flex justify-between items-center mt-6">
                <div className="text-sm text-gray-500">
                  Von {entry.creator_id === user?.id ? "dir" : "Freunden"} hinzugefügt
                </div>
                
                <div className="flex gap-2">
                  {canDelete && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 text-red-600 hover:text-red-700"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>{isDeleting ? 'Deleting...' : getDeleteButtonText(entry.creator_id, entry.user_id, user?.id || '')}</span>
                    </Button>
                  )}
                  
                  {canHide && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-1"
                      onClick={handleHide}
                      disabled={isHiding}
                    >
                      {isHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      <span>
                        {isHiding ? 'Processing...' : (isHidden ? 'Show' : 'Hide')}
                      </span>
                    </Button>
                  )}
                  
                  {entry.creator_id === user?.id && (
                    <Button variant="outline" size="sm" className="gap-1">
                      <Edit className="h-4 w-4" />
                      <span>Edit</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <ActionDrawer
        title="Delete Entry"
        description="Are you sure you want to delete this entry? This action cannot be undone."
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        actions={[
          {
            text: "Delete Entry",
            variant: "destructive",
            icon: Trash2,
            onClick: confirmDelete,
            disabled: isDeleting
          }
        ]}
        cancelText="Cancel"
      />
    </>
  );
};

export default SecureEntryDrawer;
